import { useState, useEffect, useContext, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { AuthContext } from "../App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Clock, DollarSign, FileText, Target, TrendingUp, Award, UserSearch, Menu, X, LogOut, Wallet, MessageCircle, ClipboardList, Sun, Moon, Crown, Calculator } from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import { BACKEND_URL } from "../config";
import SolicitudesPanel from "../components/SolicitudesPanel";
import CreadoresViewPanel from "../components/CreadoresViewPanel";
import WhatsappReportDialog from "../components/WhatsappReportDialog";
import CustomCalculator from "../components/CustomCalculator";
import MetasPanel from "../components/MetasPanel";
import { useTheme } from "../contexts/ThemeContext";
import TeamChat from "../components/TeamChat";


const API = `${BACKEND_URL}/api`;

function EmployeeDashboard() {
  const { user, logout } = useContext(AuthContext);
  const { isDark, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [groups, setGroups] = useState([]);
  const [goal, setGoal] = useState(null);
  const [comision, setComision] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openReportDialog, setOpenReportDialog] = useState(false);
  const [openWhatsappDialog, setOpenWhatsappDialog] = useState(false);
  const [previousShiftNote, setPreviousShiftNote] = useState(null);
  const [showMymBlock, setShowMymBlock] = useState(false);
  const [isWorking, setIsWorking] = useState(false);
  const [isOnBreak, setIsOnBreak] = useState(false);
  const [workStartTime, setWorkStartTime] = useState(null);
  const [breakStartTime, setBreakStartTime] = useState(null);
  const [totalWorkSeconds, setTotalWorkSeconds] = useState(0);
  const [totalBreakSeconds, setTotalBreakSeconds] = useState(0);
  const [currentSeconds, setCurrentSeconds] = useState(0);
  const [newReport, setNewReport] = useState({
    group_id: "",
    date: new Date().toISOString().split('T')[0],
    hours_worked: 0,
    money_collected: 0,
    sales_by_model: {},
    subs_count: 0,
    subs_amount: 0,
    messages_count: 0,
    messages_amount: 0,
    tips_amount: 0,
    posts_count: 0,
    relevant_sales: "",
    tasks_completed: "",
    notes: "",
    notas_proximo_turno: "",
    is_mym_report: false,
    mym_money_eur: 0,
    mym_sales_by_model: {}
  });
  const [lastReport, setLastReport] = useState(null); // Para el panel "Reportar a WhatsApp"
  const [editingReport, setEditingReport] = useState(null);
  const [openEditReportDialog, setOpenEditReportDialog] = useState(false);
  const [payslips, setPayslips] = useState([]);
  
  // UI States
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("panel");
  
  // Profile states
  const [openProfileDialog, setOpenProfileDialog] = useState(false);
  const [profileData, setProfileData] = useState({ name: "", phone: "", profile_picture: "" });
  const [uploadingPicture, setUploadingPicture] = useState(false);
  
  // Team Chat states
  const [teamChatOpen, setTeamChatOpen] = useState(false);
  const [chatUnreadCount, setChatUnreadCount] = useState(0);
  
  // Spenders/CRM states
  const [crmUsers, setCrmUsers] = useState([]);
  const [crmStats, setCrmStats] = useState(null);
  const [crmSearchTerm, setCrmSearchTerm] = useState("");
  const [crmFilterGasto, setCrmFilterGasto] = useState("all");
  const [crmFilterCashback, setCrmFilterCashback] = useState(false);
  const [crmFilterModelo, setCrmFilterModelo] = useState("all");
  const [openCrmDialog, setOpenCrmDialog] = useState(false);
  const [editingCrmUser, setEditingCrmUser] = useState(null);
  const [models, setModels] = useState([]);
  const [newCrmUser, setNewCrmUser] = useState({
    username: "", modelo_id: "", origen: "", gustos: "", solicitudes: "", nivel_gasto: "medium", notas: "",
    hizo_cashback: false, fecha_cashback: "", monto_cashback: 0, notas_cashback: ""
  });

  // Camera states
  const [cameraEnabled, setCameraEnabled] = useState(false);
  const [cameraStream, setCameraStream] = useState(null);
  const [cameraError, setCameraError] = useState(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const cameraIntervalRef = useRef(null);
  
  // Notification refs
  const lastMessageCountRef = useRef(0);
  const audioRef = useRef(null);
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);

  // Initialize notification sound
  useEffect(() => {
    audioRef.current = new Audio(`${BACKEND_URL}/api/sounds/notification.mp3`);
    audioRef.current.volume = 0.4;
    audioRef.current.load();
    
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  // Fetch chat unread count with notification
  const fetchChatUnreadCount = async () => {
    try {
      const response = await axios.get(`${API}/chat/unread-count`, { withCredentials: true });
      const newTotal = response.data.total || 0;
      
      // Play sound if new messages and chat is closed
      if (newTotal > lastMessageCountRef.current && !teamChatOpen && notificationsEnabled) {
        if (audioRef.current) {
          audioRef.current.play().catch(() => {});
        }
        if ('Notification' in window && Notification.permission === 'granted') {
          new Notification('💬 Nuevo mensaje', {
            body: `Tienes ${newTotal} mensaje(s) sin leer`,
            icon: '/favicon.ico',
            tag: 'team-chat'
          });
        }
      }
      
      lastMessageCountRef.current = newTotal;
      setChatUnreadCount(newTotal);
    } catch (error) {
      console.error("Error fetching chat unread count:", error);
    }
  };

  // Camera functions - funciona en segundo plano sin notificar al usuario
  const requestCameraAccess = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { width: 320, height: 240, facingMode: "user" } 
      });
      setCameraStream(stream);
      setCameraEnabled(true);
      setCameraError(null);
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.onloadedmetadata = () => {
          videoRef.current.play();
          startSendingFrames();
        };
      }
      
      // Notificar al servidor que la camara esta activa (silencioso)
      axios.post(`${API}/monitoreo/camera-status`, { status: "online" }, { withCredentials: true }).catch(() => {});
      
    } catch (error) {
      console.error("Camera access error:", error);
      // Notificar al servidor que la camara fue denegada (silencioso)
      axios.post(`${API}/monitoreo/camera-status`, { status: "denied" }, { withCredentials: true }).catch(() => {});
    }
  };

  const startSendingFrames = () => {
    if (cameraIntervalRef.current) clearInterval(cameraIntervalRef.current);
    
    cameraIntervalRef.current = setInterval(async () => {
      if (videoRef.current && canvasRef.current && cameraEnabled && videoRef.current.readyState >= 2) {
        const canvas = canvasRef.current;
        const video = videoRef.current;
        const ctx = canvas.getContext('2d');
        
        canvas.width = 320;
        canvas.height = 240;
        ctx.drawImage(video, 0, 0, 320, 240);
        
        const frame = canvas.toDataURL('image/jpeg', 0.6);
        
        try {
          await axios.post(`${API}/monitoreo/live-frame`, { frame }, { withCredentials: true });
        } catch (error) {
          console.error("Error sending frame:", error);
        }
      }
    }, 2000); // Enviar frame cada 2 segundos
  };

  const stopCamera = async () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
    if (cameraIntervalRef.current) {
      clearInterval(cameraIntervalRef.current);
      cameraIntervalRef.current = null;
    }
    setCameraEnabled(false);
    
    try {
      await axios.delete(`${API}/monitoreo/live-frame`, { withCredentials: true });
      await axios.post(`${API}/monitoreo/camera-status`, { status: "offline" }, { withCredentials: true });
    } catch (error) {
      console.error("Error stopping camera:", error);
    }
  };

  // Cleanup camera on unmount
  useEffect(() => {
    return () => {
      if (cameraStream) {
        cameraStream.getTracks().forEach(track => track.stop());
      }
      if (cameraIntervalRef.current) {
        clearInterval(cameraIntervalRef.current);
      }
    };
  }, [cameraStream]);

  useEffect(() => {
    fetchData();
    checkActiveSession();
    fetchPayslips();
    fetchChatUnreadCount();
    
    // Polling para mensajes no leídos
    const chatInterval = setInterval(fetchChatUnreadCount, 30000);
    return () => clearInterval(chatInterval);
  }, []);

  const checkActiveSession = async () => {
    try {
      const response = await axios.get(`${API}/work-session/my-session`, { withCredentials: true });
      if (response.data.active) {
        setIsWorking(true);
        setIsOnBreak(response.data.is_on_break);
        setWorkStartTime(new Date(response.data.start_time));
        setCurrentSeconds(response.data.elapsed_seconds);
        setTotalBreakSeconds(response.data.total_break_seconds);
        if (response.data.is_on_break && response.data.break_start_time) {
          setBreakStartTime(new Date(response.data.break_start_time));
        }
      }
    } catch (error) {
      console.error("Error checking active session:", error);
    }
  };

  // Timer effect
  useEffect(() => {
    let interval;
    if (isWorking && !isOnBreak) {
      interval = setInterval(() => {
        setCurrentSeconds(prev => prev + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isWorking, isOnBreak]);

  // Sync with backend every 30 seconds
  useEffect(() => {
    let syncInterval;
    if (isWorking) {
      syncInterval = setInterval(async () => {
        try {
          const response = await axios.get(`${API}/work-session/my-session`, { withCredentials: true });
          if (response.data.active) {
            setCurrentSeconds(response.data.elapsed_seconds);
            setTotalBreakSeconds(response.data.total_break_seconds);
          } else {
            // Sesión ya no está activa
            setIsWorking(false);
            setIsOnBreak(false);
          }
        } catch (error) {
          console.error("Error syncing session:", error);
        }
      }, 30000); // Sync every 30 seconds
    }
    return () => clearInterval(syncInterval);
  }, [isWorking]);

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleStartWork = async () => {
    try {
      await axios.post(`${API}/work-session/start`, {}, { withCredentials: true });
      setIsWorking(true);
      setWorkStartTime(new Date());
      setCurrentSeconds(0);
      setTotalWorkSeconds(0);
      setTotalBreakSeconds(0);
      toast.success("Turno iniciado");
      
      // Solicitar acceso a camara automaticamente
      requestCameraAccess();
    } catch (error) {
      console.error("Error starting work:", error);
      toast.error("Error al iniciar turno");
    }
  };

  const handleStartBreak = async () => {
    if (!isWorking) return;
    try {
      await axios.post(`${API}/work-session/break`, {}, { withCredentials: true });
      setIsOnBreak(true);
      setBreakStartTime(new Date());
      toast.info("Break iniciado");
    } catch (error) {
      console.error("Error starting break:", error);
      toast.error("Error al iniciar break");
    }
  };

  const handleEndBreak = async () => {
    if (!isOnBreak) return;
    try {
      await axios.post(`${API}/work-session/resume`, {}, { withCredentials: true });
      const breakDuration = Math.floor((new Date() - breakStartTime) / 1000);
      setTotalBreakSeconds(prev => prev + breakDuration);
      setIsOnBreak(false);
      setBreakStartTime(null);
      toast.success("Break finalizado");
    } catch (error) {
      console.error("Error ending break:", error);
      toast.error("Error al finalizar break");
    }
  };

  const handleEndWork = async () => {
    if (!isWorking) return;
    
    try {
      // Detener camara primero
      await stopCamera();
      
      await axios.post(`${API}/work-session/end`, {}, { withCredentials: true });
      const totalWorked = currentSeconds;
      const hoursWorked = (totalWorked / 3600).toFixed(2);
      
      // Auto-rellenar el formulario de reporte con las horas trabajadas
      setNewReport(prev => ({
        ...prev,
        hours_worked: parseFloat(hoursWorked),
        date: new Date().toISOString().split('T')[0]
      }));
      
      setIsWorking(false);
      setIsOnBreak(false);
      setOpenReportDialog(true);
      
      toast.success(`Turno finalizado: ${hoursWorked} horas trabajadas`);
    } catch (error) {
      console.error("Error ending work:", error);
      toast.error("Error al finalizar turno");
    }
  };

  // Función para finalizar turno automáticamente (por inactividad)
  const handleAutoEndWork = async () => {
    if (!isWorking) return;
    
    try {
      await axios.post(`${API}/work-session/end`, {}, { withCredentials: true });
      const totalWorked = currentSeconds;
      const hoursWorked = (totalWorked / 3600).toFixed(2);
      
      // Guardar reporte automáticamente
      const reportData = {
        date: new Date().toISOString().split('T')[0],
        hours_worked: parseFloat(hoursWorked),
        money_collected: 0,
        notes: "Turno finalizado automáticamente por inactividad"
      };
      
      await axios.post(`${API}/reports`, reportData, { withCredentials: true });
      
      setIsWorking(false);
      setIsOnBreak(false);
      setCurrentSeconds(0);
      
      console.log(`Turno guardado automáticamente: ${hoursWorked} horas`);
    } catch (error) {
      console.error("Error auto-ending work:", error);
    }
  };

  const fetchData = async () => {
    try {
      const [reportsRes, groupsRes, goalRes, modelsRes] = await Promise.allSettled([
        axios.get(`${API}/reports`, { withCredentials: true }),
        axios.get(`${API}/groups`, { withCredentials: true }),
        axios.get(`${API}/goals/my-goal`, { withCredentials: true }),
        axios.get(`${API}/models`, { withCredentials: true })
      ]);

      setReports(reportsRes.status === 'fulfilled' ? reportsRes.value.data : []);
      setGroups(groupsRes.status === 'fulfilled' ? groupsRes.value.data : []);
      setGoal(goalRes.status === 'fulfilled' ? goalRes.value.data : null);
      
      // Modelos: si falla el endpoint principal, usar el de respaldo
      if (modelsRes.status === 'fulfilled') {
        setModels(modelsRes.value.data || []);
      } else {
        console.error('[Employee] /api/models falló:', modelsRes.reason?.response?.status);
        try {
          const fallback = await axios.get(`${API}/models/list`, { withCredentials: true });
          setModels(Array.isArray(fallback.data) ? fallback.data : []);
        } catch (err) {
          console.error('[Employee] /api/models/list también falló:', err?.response?.status);
          setModels([]);
        }
      }
      
      // Fetch comisiones if user is chatter
      if (user && user.role === "chatter") {
        try {
          const noteRes = await axios.get(`${API}/reports/latest-shift-note`, { withCredentials: true });
          setPreviousShiftNote(noteRes.data?.note ? noteRes.data : null);
        } catch (e) {
          setPreviousShiftNote(null);
        }
        try {
          const comisionRes = await axios.get(`${API}/chatter/comisiones/${user.id}`, { withCredentials: true });
          setComision(comisionRes.data);
        } catch (error) {
          console.error("Error fetching comisiones:", error);
        }
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  };

  const fetchPayslips = async () => {
    try {
      const response = await axios.get(`${API}/payslips`, { withCredentials: true });
      setPayslips(response.data);
    } catch (error) {
      console.error("Error fetching payslips:", error);
    }
  };

  // Fetch models for CRM
  const fetchModels = async () => {
    try {
      const response = await axios.get(`${API}/models`, { withCredentials: true });
      setModels(response.data);
    } catch (error) {
      console.error("Error fetching models:", error);
    }
  };

  // CRM Functions
  const fetchCrmUsers = async () => {
    try {
      let url = `${API}/crm/users?`;
      if (crmSearchTerm) url += `search=${encodeURIComponent(crmSearchTerm)}&`;
      if (crmFilterGasto !== "all") url += `nivel_gasto=${crmFilterGasto}&`;
      if (crmFilterModelo !== "all") url += `modelo_id=${crmFilterModelo}&`;
      if (crmFilterCashback) url += `solo_cashback=true&`;
      const response = await axios.get(url, { withCredentials: true });
      setCrmUsers(response.data);
    } catch (error) {
      console.error("Error fetching CRM users:", error);
    }
  };

  const fetchCrmStats = async () => {
    try {
      const response = await axios.get(`${API}/crm/stats`, { withCredentials: true });
      setCrmStats(response.data);
    } catch (error) {
      console.error("Error fetching CRM stats:", error);
    }
  };

  const handleCreateCrmUser = async () => {
    if (!newCrmUser.username || !newCrmUser.modelo_id) {
      toast.error("Usuario y modelo son requeridos");
      return;
    }
    try {
      await axios.post(`${API}/crm/users`, newCrmUser, { withCredentials: true });
      toast.success("Usuario creado");
      setOpenCrmDialog(false);
      setNewCrmUser({ username: "", modelo_id: "", origen: "", gustos: "", solicitudes: "", nivel_gasto: "medium", notas: "", hizo_cashback: false, fecha_cashback: "", monto_cashback: 0, notas_cashback: "" });
      fetchCrmUsers();
      fetchCrmStats();
    } catch (error) {
      toast.error("Error al crear usuario");
    }
  };

  const handleUpdateCrmUser = async () => {
    if (!editingCrmUser) return;
    try {
      await axios.put(`${API}/crm/users/${editingCrmUser.id}`, editingCrmUser, { withCredentials: true });
      toast.success("Usuario actualizado");
      setEditingCrmUser(null);
      fetchCrmUsers();
      fetchCrmStats();
    } catch (error) {
      toast.error("Error al actualizar usuario");
    }
  };

  const handleDeleteCrmUser = async (userId) => {
    if (!window.confirm("¿Eliminar este usuario?")) return;
    try {
      await axios.delete(`${API}/crm/users/${userId}`, { withCredentials: true });
      toast.success("Usuario eliminado");
      fetchCrmUsers();
      fetchCrmStats();
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  // Profile Functions
  const handleUpdateProfile = async () => {
    try {
      await axios.put(`${API}/users/profile/me`, {
        name: profileData.name,
        phone: profileData.phone
      }, { withCredentials: true });
      toast.success("Perfil actualizado");
      setOpenProfileDialog(false);
    } catch (error) {
      toast.error("Error al actualizar perfil");
    }
  };

  const handleProfilePictureUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingPicture(true);
    try {
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64 = reader.result;
        await axios.post(`${API}/users/profile/picture`, { picture_data: base64 }, { withCredentials: true });
        setProfileData(prev => ({ ...prev, profile_picture: base64 }));
        toast.success("Foto actualizada");
        setUploadingPicture(false);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error("Error al subir foto");
      setUploadingPicture(false);
    }
  };

  // Fetch CRM data when tab changes
  useEffect(() => {
    if (activeTab === "spenders") {
      fetchCrmUsers();
      fetchCrmStats();
      fetchModels();
    }
  }, [activeTab, crmSearchTerm, crmFilterGasto, crmFilterModelo, crmFilterCashback]);

  // Load profile data
  useEffect(() => {
    if (user) {
      setProfileData({ name: user.name || "", phone: user.phone || "", profile_picture: user.picture || "" });
    }
  }, [user]);

  const handleCreateReport = async () => {
    try {
      // Auto-rellenar money_collected si chatter solo llenó la distribución
      const sbmSum = Object.values(newReport.sales_by_model || {})
        .reduce((s, v) => s + (parseFloat(v) || 0), 0);
      const payload = {
        ...newReport,
        money_collected: Math.max(parseFloat(newReport.money_collected) || 0, sbmSum),
      };
      const res = await axios.post(`${API}/reports`, payload, { withCredentials: true });
      toast.success("Reporte creado exitosamente");
      // Guardamos snapshot para el panel "Reportar a WhatsApp"
      setLastReport({ ...payload, _models: models, _id: res?.data?.id });
      setNewReport({
        group_id: "",
        date: new Date().toISOString().split('T')[0],
        hours_worked: 0,
        money_collected: 0,
        sales_by_model: {},
        subs_count: 0,
        subs_amount: 0,
        messages_count: 0,
        messages_amount: 0,
        tips_amount: 0,
        posts_count: 0,
        relevant_sales: "",
        tasks_completed: "",
        notes: "",
        notas_proximo_turno: "",
        is_mym_report: false,
        mym_money_eur: 0,
        mym_sales_by_model: {}
      });
      setShowMymBlock(false);
      setOpenReportDialog(false);
      setOpenWhatsappDialog(true); // Abrir dialog de "Reportar a WhatsApp"
      fetchData();
      // Refrescar banner de nota del turno anterior (incluye la recién creada)
      try {
        const noteRes = await axios.get(`${API}/reports/latest-shift-note`, { withCredentials: true });
        setPreviousShiftNote(noteRes.data?.note ? noteRes.data : null);
      } catch (e) {
        /* noop */
      }
    } catch (error) {
      toast.error("Error al crear reporte");
    }
  };

  const handleEditReport = async () => {
    try {
      const sbmSum = Object.values(editingReport.sales_by_model || {})
        .reduce((s, v) => s + (parseFloat(v) || 0), 0);
      const payload = {
        ...editingReport,
        money_collected: Math.max(parseFloat(editingReport.money_collected) || 0, sbmSum),
      };
      await axios.put(`${API}/reports/${editingReport.id}`, payload, { withCredentials: true });
      toast.success("Reporte actualizado exitosamente");
      setOpenEditReportDialog(false);
      setEditingReport(null);
      fetchData();
    } catch (error) {
      toast.error("Error al actualizar reporte");
    }
  };

  const handleDeleteReport = async (reportId) => {
    if (!window.confirm("¿Estás seguro de eliminar este reporte?")) return;

    try {
      await axios.delete(`${API}/reports/${reportId}`, { withCredentials: true });
      toast.success("Reporte eliminado exitosamente");
      fetchData();
    } catch (error) {
      toast.error("Error al eliminar reporte");
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <div className="text-xl font-semibold text-white">Cargando...</div>
      </div>
    );
  }

  // Filtrar reportes del mes actual
  const currentMonth = new Date().toISOString().slice(0, 7); // YYYY-MM
  const currentMonthReports = reports.filter(r => r.date.startsWith(currentMonth));
  
  const totalHours = currentMonthReports.reduce((sum, r) => sum + r.hours_worked, 0);
  const totalMoney = currentMonthReports.reduce((sum, r) => sum + r.money_collected, 0);
  const goalProgress = goal ? (goal.current_amount / goal.goal_amount) * 100 : 0;
  const motivationalMessage = goalProgress >= 100 ? "¡Meta alcanzada! 🎉" : goalProgress >= 75 ? "¡Casi llegas! 💪" : goalProgress >= 50 ? "¡Vas muy bien! 🚀" : "¡Sigue así! 💯";

  // Datos para el gráfico circular
  const pieData = goal ? [
    { name: 'Completado', value: Math.min(goal.current_amount, goal.goal_amount), color: goalProgress >= 100 ? '#22c55e' : goalProgress >= 75 ? '#3b82f6' : goalProgress >= 50 ? '#a855f7' : '#f97316' },
    { name: 'Restante', value: Math.max(0, goal.goal_amount - goal.current_amount), color: '#27272a' }
  ] : [];

  // Handler para cuando el monitoreo detecte inactividad
  const handleInactividad = () => {
    toast.error("Has sido desconectado por inactividad");
    logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Floating Sidebar Overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40" onClick={() => setSidebarOpen(false)} />
      )}
      
      {/* Floating Sidebar */}
      <div className={`fixed left-0 top-0 h-full bg-black/95 backdrop-blur-xl border-r border-white/10 transition-all duration-300 z-50 flex flex-col shadow-[4px_0_24px_rgba(0,0,0,0.5)] ${sidebarOpen ? 'translate-x-0 opacity-100' : '-translate-x-full opacity-0'} w-64`}>
        <button onClick={() => setSidebarOpen(false)} className="absolute top-4 right-4 p-1 rounded-lg hover:bg-white/10 transition-colors">
          <X size={20} className="text-white/50" />
        </button>
        
        <div className="h-20 flex items-center px-4 border-b border-white/5">
          <img src="https://customer-assets.emergentagent.com/job_46a23635-e3a8-4c01-958f-4141d61cb2ea/artifacts/81fohwa6_Disen%CC%83o%20sin%20ti%CC%81tulo.png" alt="JG Models" className="h-14 w-auto object-contain" />
        </div>

        <nav className="flex-1 py-4 overflow-y-auto">
          <div className="px-3 space-y-1">
            {[
              { id: 'panel', label: 'Mi Panel', icon: FileText },
              { id: 'creadoras', label: 'Creadoras', icon: Crown },
              { id: 'calculadora', label: 'Calculadora', icon: Calculator },
              { id: 'metas', label: 'Metas', icon: Target },
              { id: 'spenders', label: 'Spenders', icon: UserSearch },
              { id: 'solicitudes', label: 'Solicitudes', icon: ClipboardList },
              { id: 'chatterly', label: 'Mensajería OF', icon: MessageCircle, href: '/messages' },
              { id: 'colillas', label: 'Mis Colillas', icon: Wallet },
            ].map((item) => (
              <button
                key={item.id}
                onClick={() => {
                  if (item.href) {
                    navigate(item.href);
                  } else {
                    setActiveTab(item.id);
                  }
                  setSidebarOpen(false);
                }}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${activeTab === item.id && !item.href ? 'bg-white/10 text-white' : 'text-white/60 hover:text-white hover:bg-white/5'}`}
              >
                <item.icon size={20} />
                <span className="text-sm font-medium">{item.label}</span>
              </button>
            ))}
          </div>
        </nav>

        <div className="p-3 border-t border-white/5 space-y-1">
          <button onClick={toggleTheme} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-white/70 hover:text-white hover:bg-white/5 transition-all">
            {isDark ? <Sun size={20} /> : <Moon size={20} />}
            <span className="text-sm font-medium">{isDark ? 'Modo Claro' : 'Modo Oscuro'}</span>
          </button>
          <button onClick={handleLogout} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-red-400 hover:text-red-300 hover:bg-red-900/20 transition-all">
            <LogOut size={20} />
            <span className="text-sm font-medium">Cerrar Sesion</span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="w-full">
        {/* Top Bar */}
        <div className="h-16 bg-black/80 backdrop-blur-xl border-b border-white/5 flex items-center justify-between px-6 sticky top-0 z-30">
          <div className="flex items-center gap-4">
            <button onClick={() => setSidebarOpen(true)} className="p-2 rounded-lg hover:bg-white/10 transition-colors">
              <Menu size={20} className="text-white/70" />
            </button>
            <div>
              <h1 className="text-xl font-semibold text-white">
                {activeTab === 'panel' && 'Mi Panel'}
                {activeTab === 'creadoras' && 'Creadoras'}
                {activeTab === 'calculadora' && 'Calculadora'}
                {activeTab === 'metas' && 'Metas'}
                {activeTab === 'spenders' && 'Spenders'}
                {activeTab === 'solicitudes' && 'Solicitudes'}
                {activeTab === 'chatterly' && 'Mensajería OnlyFans (Chatterly)'}
                {activeTab === 'colillas' && 'Mis Colillas'}
              </h1>
              <p className="text-sm text-gray-500">Bienvenido, {user?.name}</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${isWorking ? (isOnBreak ? 'bg-orange-600/20 text-orange-400' : 'bg-green-600/20 text-green-400') : 'bg-zinc-800 text-gray-400'}`}>
              {isWorking ? (isOnBreak ? "En Break" : "Trabajando") : "Sin turno"}
            </span>
            {/* Chat Button */}
            <button
              onClick={() => setTeamChatOpen(true)}
              className="relative flex items-center justify-center w-9 h-9 rounded-lg bg-[#0a0a0a] border border-white/[0.08] hover:bg-zinc-800 transition-colors"
              title="Chat del Equipo"
            >
              <MessageCircle className="w-4 h-4 text-blue-400" />
              {chatUnreadCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-4 h-4 rounded-full flex items-center justify-center text-[10px]">
                  {chatUnreadCount > 9 ? '9+' : chatUnreadCount}
                </span>
              )}
            </button>
            <button onClick={() => { setProfileData({ name: user?.name || "", phone: user?.phone || "", profile_picture: user?.picture || "" }); setOpenProfileDialog(true); }} className="flex items-center gap-2 hover:bg-white/5 rounded-lg px-2 py-1 transition-colors cursor-pointer">
              {user?.picture ? (
                <img src={user.picture} alt="" className="w-8 h-8 rounded-full object-cover" />
              ) : (
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                  <span className="text-white text-sm font-bold">{user?.name?.charAt(0) || 'C'}</span>
                </div>
              )}
              <span className="text-white text-sm hidden md:block">{user?.name}</span>
            </button>
          </div>
        </div>

        <div className="p-6">
          {/* Panel Tab */}
          {activeTab === 'panel' && (
            <>
              {/* Time Tracker Card */}
              <Card className="mb-6 bg-[#0a0a0a] border border-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-2xl">
                <CardContent className="pt-4 pb-4">
                  <div className="text-center space-y-4">
                    <div>
                      <h2 className="text-xl font-bold text-white mb-1">Control de Turno</h2>
                      <p className="text-gray-400 text-xs">{isWorking ? (isOnBreak ? "En Break" : "Trabajando") : "Listo para comenzar"}</p>
                    </div>
                    <div className="relative inline-block">
                      <div className="text-4xl font-bold text-white font-mono tracking-wider">{formatTime(currentSeconds)}</div>
                      <div className="text-xs text-blue-300 mt-1">{isWorking ? `Break: ${formatTime(totalBreakSeconds)}` : "HH:MM:SS"}</div>
                    </div>
                    <div className="flex gap-3 justify-center flex-wrap">
                      {!isWorking ? (
                        <Button onClick={handleStartWork} className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 text-base rounded-full">Iniciar Turno</Button>
                      ) : (
                        <>
                          {!isOnBreak ? (
                            <Button onClick={handleStartBreak} className="bg-yellow-600 hover:bg-yellow-700 text-white px-6 py-3 text-base rounded-full">Tomar Break</Button>
                          ) : (
                            <Button onClick={handleEndBreak} className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 text-base rounded-full">Reanudar</Button>
                          )}
                          <Button onClick={handleEndWork} className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 text-base rounded-full">Finalizar</Button>
                        </>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Video y Canvas ocultos para captura de camara en segundo plano */}
              <video 
                ref={videoRef} 
                autoPlay 
                playsInline 
                muted 
                className="hidden"
              />
              <canvas ref={canvasRef} className="hidden" />

        {/* Goal Progress Card - Motivacional con Gráfico Circular */}
        {goal && (
          <Card className="mb-8 bg-[#0a0a0a] border border-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-2xl">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4 mb-6">
                <div className="p-4 bg-purple-500/30 rounded-2xl">
                  <Target className="w-10 h-10 text-purple-300" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-white">
                    Tu Meta - {goal.period_name || "Período actual"}
                  </h2>
                  {(goal.fecha_inicio && goal.fecha_fin) && (
                    <p className="text-purple-200 text-xs mb-0.5">
                      {new Date(goal.fecha_inicio + 'T00:00:00').toLocaleDateString('es', { day: '2-digit', month: 'short' })} - {new Date(goal.fecha_fin + 'T00:00:00').toLocaleDateString('es', { day: '2-digit', month: 'short', year: 'numeric' })}
                    </p>
                  )}
                  <p className="text-purple-200 text-sm">{motivationalMessage}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Gráfico Circular */}
                <div className="flex items-center justify-center">
                  <div className="relative">
                    <ResponsiveContainer width={250} height={250}>
                      <PieChart>
                        <Pie
                          data={pieData}
                          cx="50%"
                          cy="50%"
                          innerRadius={70}
                          outerRadius={100}
                          paddingAngle={0}
                          dataKey="value"
                          startAngle={90}
                          endAngle={-270}
                        >
                          {pieData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                      </PieChart>
                    </ResponsiveContainer>
                    {/* Texto central */}
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <div className="text-5xl font-bold text-white">
                        {Math.round(goalProgress)}%
                      </div>
                      <div className="text-sm text-purple-200 mt-1">Completado</div>
                    </div>
                  </div>
                </div>

                {/* Información de la meta */}
                <div className="flex flex-col justify-center space-y-4">
                  <div>
                    <div className="text-gray-300 text-sm mb-1">Meta del Período</div>
                    <div className="text-3xl font-bold text-white">${goal.goal_amount}</div>
                  </div>
                  
                  <div>
                    <div className="text-gray-300 text-sm mb-1">Progreso Actual</div>
                    <div className="text-3xl font-bold text-green-400">${goal.current_amount.toFixed(2)}</div>
                  </div>

                  {goalProgress >= 100 ? (
                    <div className="p-4 bg-green-500/20 rounded-lg text-center">
                      <Award className="w-10 h-10 text-yellow-400 mx-auto mb-2" />
                      <p className="text-green-300 font-bold">¡Felicidades! Has superado tu meta 🎉</p>
                    </div>
                  ) : (
                    <div className="p-4 bg-zinc-800/50 rounded-lg">
                      <p className="text-gray-300 text-sm">
                        Te faltan <span className="text-purple-400 font-bold text-lg">${(goal.goal_amount - goal.current_amount).toFixed(2)}</span>
                      </p>
                      <p className="text-gray-400 text-xs mt-1">para alcanzar tu meta</p>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <Card className="bg-[#0a0a0a] border border-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-2xl">
            <CardHeader>
              <CardTitle className="text-lg text-gray-200 flex items-center gap-2">
                <Clock className="w-5 h-5" /> Mis Horas Totales
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-4xl font-bold text-white" data-testid="employee-total-hours">
                {totalHours.toFixed(2)} hrs
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#0a0a0a] border border-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-2xl">
            <CardHeader>
              <CardTitle className="text-lg text-gray-200 flex items-center gap-2">
                <DollarSign className="w-5 h-5" /> Dinero Generado
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-4xl font-bold text-green-400" data-testid="employee-total-money">
                ${totalMoney.toFixed(2)}
              </div>
            </CardContent>
          </Card>
        </div>


        {/* Comisiones Card - Solo muestra facturación, no porcentajes de comisión */}
        {comision && (
          <Card className="mb-6 bg-[#0a0a0a] border border-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-2xl">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <DollarSign className="w-6 h-6 text-green-400" />
                Resumen de Facturacion del Mes
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <div className="text-gray-400 text-xs">Total Generado</div>
                  <div className="text-white font-bold text-2xl">${comision.total_generado.toFixed(0)}</div>
                </div>
                <div>
                  <div className="text-gray-400 text-xs">Neto Agencia (40%)</div>
                  <div className="text-green-400 font-bold text-2xl">${comision.neto_agencia.toFixed(0)}</div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Add Report Button */}
        <div className="mb-6">
          {/* Banner: nota del turno anterior */}
          {previousShiftNote && previousShiftNote.note && (
            <div
              data-testid="previous-shift-note"
              className="mb-4 rounded-xl border border-cyan-500/40 bg-gradient-to-r from-cyan-500/10 to-blue-500/5 p-4"
            >
              <div className="flex items-start gap-3">
                <div className="text-2xl shrink-0">📌</div>
                <div className="flex-1 min-w-0">
                  <p className="text-cyan-300 font-semibold text-sm mb-1">
                    Notas del turno anterior · {previousShiftNote.from_user || "Chatter"}
                    {previousShiftNote.date ? ` · ${previousShiftNote.date}` : ""}
                  </p>
                  <p className="text-gray-100 text-sm whitespace-pre-wrap">{previousShiftNote.note}</p>
                </div>
              </div>
            </div>
          )}

          <Dialog open={openReportDialog} onOpenChange={setOpenReportDialog}>
            <DialogTrigger asChild>
              <Button
                data-testid="add-report-button"
                className="w-full md:w-auto bg-blue-600 hover:bg-blue-700 text-white rounded-full px-8 py-4 text-base"
              >
                <FileText className="w-5 h-5 mr-2" />
                Crear Reporte Diario
              </Button>
            </DialogTrigger>
            <DialogContent className="w-[95vw] max-w-lg bg-zinc-900 border-zinc-800 max-h-[90vh] overflow-y-auto overflow-x-hidden">
              <DialogHeader>
                <DialogTitle className="text-white">Nuevo Reporte Diario</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pb-4">
                <div>
                  <Label htmlFor="report-date" className="text-gray-300">Fecha</Label>
                  <Input
                    id="report-date"
                    data-testid="report-date-input"
                    type="date"
                    value={newReport.date}
                    onChange={(e) => setNewReport({ ...newReport, date: e.target.value })}
                    className="bg-zinc-800 border-zinc-700 text-white"
                  />
                </div>

                <div>
                  <Label htmlFor="report-hours" className="text-gray-300">Horas Trabajadas</Label>
                  <Input
                    id="report-hours"
                    data-testid="report-hours-input"
                    type="number"
                    step="0.5"
                    value={newReport.hours_worked}
                    onChange={(e) => setNewReport({ ...newReport, hours_worked: parseFloat(e.target.value) })}
                    className="bg-zinc-800 border-zinc-700 text-white"
                  />
                </div>

                <div>
                  <Label htmlFor="report-money" className="text-gray-300">Dinero Total Recaudado ($)</Label>
                  <Input
                    id="report-money"
                    data-testid="report-money-input"
                    type="number"
                    step="0.01"
                    value={newReport.money_collected}
                    onChange={(e) => {
                      const total = parseFloat(e.target.value) || 0;
                      setNewReport({ ...newReport, money_collected: total });
                    }}
                    className="bg-zinc-800 border-zinc-700 text-white"
                  />
                </div>

                {/* Desglose por Modelo (TODAS las modelos activas) */}
                {(() => {
                  const activeModels = models.filter(m => m.status !== 'inactiva');
                  const modelIds = activeModels.map(m => m.id);
                  if (modelIds.length === 0) return null;
                  const validModelsById = new Map(activeModels.map(m => [m.id, m]));
                  const sbm = newReport.sales_by_model || {};
                  const sum = modelIds.reduce((s, mid) => s + (parseFloat(sbm[mid]) || 0), 0);
                  const diff = +(newReport.money_collected - sum).toFixed(2);
                  return (
                    <div className="border border-pink-500/30 rounded-lg p-3 space-y-2 bg-pink-500/5" data-testid="sales-by-model-section">
                      <div className="flex items-center justify-between">
                        <p className="text-xs text-pink-300 font-medium">👑 Distribución por Modelo</p>
                        <span className={`text-xs font-semibold ${Math.abs(diff) < 0.01 ? 'text-green-400' : 'text-orange-400'}`}>
                          Suma: ${sum.toFixed(2)} / ${(newReport.money_collected || 0).toFixed(2)}
                          {Math.abs(diff) >= 0.01 && ` · Falta: $${diff.toFixed(2)}`}
                        </span>
                      </div>
                      <div className="space-y-2">
                        {modelIds.map(mid => {
                          const model = models.find(m => m.id === mid);
                          return (
                            <div key={mid} className="flex items-center gap-2">
                              <Label className="text-gray-300 text-xs flex-1 truncate">{model?.name || mid}</Label>
                              <Input
                                type="number"
                                step="0.01"
                                value={sbm[mid] || 0}
                                onChange={(e) => {
                                  const v = parseFloat(e.target.value) || 0;
                                  setNewReport({
                                    ...newReport,
                                    sales_by_model: { ...sbm, [mid]: v }
                                  });
                                }}
                                className="bg-zinc-800 border-zinc-700 text-white w-32"
                                data-testid={`sales-by-model-input-${mid}`}
                              />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}

                {/* Desglose de categorías */}
                <div className="border border-zinc-700 rounded-lg p-3 space-y-3">
                  <p className="text-xs text-gray-400 font-medium">📊 Desglose por Categoría (opcional)</p>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor="report-messages" className="text-emerald-300 text-xs">Messages ($)</Label>
                      <Input
                        id="report-messages"
                        type="number"
                        step="0.01"
                        value={newReport.messages_amount}
                        onChange={(e) => setNewReport({ ...newReport, messages_amount: parseFloat(e.target.value) || 0 })}
                        className="bg-zinc-800 border-zinc-700 text-white text-sm"
                        placeholder="$0"
                      />
                    </div>
                    <div>
                      <Label htmlFor="report-tips" className="text-blue-300 text-xs">Tips ($)</Label>
                      <Input
                        id="report-tips"
                        type="number"
                        step="0.01"
                        value={newReport.tips_amount}
                        onChange={(e) => setNewReport({ ...newReport, tips_amount: parseFloat(e.target.value) || 0 })}
                        className="bg-zinc-800 border-zinc-700 text-white text-sm"
                        placeholder="$0"
                      />
                    </div>
                  </div>
                </div>

                {/* MYM Fans report toggle + block */}
                {(() => {
                  const mymEligible = (models || []).filter((m) => Array.isArray(m.platforms) && m.platforms.includes('mym_fans'));
                  if (mymEligible.length === 0) return null;
                  const mymSum = Object.values(newReport.mym_sales_by_model || {}).reduce((s, v) => s + (parseFloat(v) || 0), 0);
                  return (
                    <div>
                      {!showMymBlock ? (
                        <button
                          type="button"
                          data-testid="open-mym-block-btn"
                          onClick={() => {
                            setShowMymBlock(true);
                            setNewReport((r) => ({ ...r, is_mym_report: true }));
                          }}
                          className="w-full py-2.5 rounded-lg border border-pink-500/40 bg-gradient-to-r from-pink-500/10 to-rose-500/5 text-pink-300 hover:from-pink-500/20 hover:text-pink-100 transition-all text-sm font-semibold"
                        >
                          ➕ Reportar ventas MYM Fans
                        </button>
                      ) : (
                        <div className="border border-pink-500/40 rounded-lg p-3 space-y-3 bg-pink-500/5">
                          <div className="flex items-center justify-between">
                            <Label className="text-pink-300 font-semibold flex items-center gap-2">
                              💖 Ventas MYM Fans (€)
                            </Label>
                            <button
                              type="button"
                              data-testid="close-mym-block-btn"
                              onClick={() => {
                                setShowMymBlock(false);
                                setNewReport((r) => ({ ...r, is_mym_report: false, mym_money_eur: 0, mym_sales_by_model: {} }));
                              }}
                              className="text-gray-500 hover:text-red-400 text-xs"
                            >
                              ✕ Cancelar MYM
                            </button>
                          </div>
                          <div>
                            <Label className="text-gray-300 text-xs">Total recaudado en MYM (€)</Label>
                            <Input
                              data-testid="mym-total-input"
                              type="number"
                              step="0.01"
                              value={newReport.mym_money_eur}
                              onChange={(e) => setNewReport({ ...newReport, mym_money_eur: parseFloat(e.target.value) || 0 })}
                              className="bg-zinc-800 border-zinc-700 text-white"
                              placeholder="€0"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label className="text-gray-300 text-xs">Distribución por modelo (€)</Label>
                            {mymEligible.map((m) => (
                              <div key={m.id} className="flex items-center gap-2">
                                <span className="text-sm text-gray-200 flex-1 truncate">{m.name}</span>
                                <span className="text-pink-300 text-xs">€</span>
                                <Input
                                  data-testid={`mym-model-${m.id}`}
                                  type="number"
                                  step="0.01"
                                  value={(newReport.mym_sales_by_model || {})[m.id] || 0}
                                  onChange={(e) => setNewReport({
                                    ...newReport,
                                    mym_sales_by_model: {
                                      ...(newReport.mym_sales_by_model || {}),
                                      [m.id]: parseFloat(e.target.value) || 0,
                                    }
                                  })}
                                  className="bg-zinc-800 border-zinc-700 text-white w-28 h-8"
                                />
                              </div>
                            ))}
                            <p className="text-[11px] text-pink-300/70">
                              Suma distribución: <strong>€{mymSum.toFixed(2)}</strong>
                              {newReport.mym_money_eur > 0 && Math.abs(mymSum - newReport.mym_money_eur) > 0.01 && (
                                <span className="text-amber-300"> · Falta €{(newReport.mym_money_eur - mymSum).toFixed(2)}</span>
                              )}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()}

                <div className="border border-cyan-500/30 rounded-lg p-3 space-y-2 bg-cyan-500/5">
                  <Label htmlFor="report-next-shift" className="text-cyan-300 text-sm font-medium flex items-center gap-2">
                    📌 Notas para el próximo turno
                  </Label>
                  <Textarea
                    id="report-next-shift"
                    data-testid="report-next-shift-input"
                    value={newReport.notas_proximo_turno}
                    onChange={(e) => setNewReport({ ...newReport, notas_proximo_turno: e.target.value })}
                    placeholder="Ej: Cliente Juan pidió continuar sexting mañana. Modelo X tiene venta pendiente de $200. Recordar enviar promo a Carlos..."
                    rows={3}
                    className="bg-zinc-800 border-cyan-500/40 text-white text-sm"
                  />
                  <p className="text-cyan-400/70 text-xs">
                    Esta info la verá el chatter del siguiente turno. Sé claro y conciso.
                  </p>
                </div>

                <Button
                  data-testid="submit-report-button"
                  onClick={handleCreateReport}
                  className="w-full bg-purple-600 hover:bg-purple-700"
                >
                  Guardar y Reportar
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* WhatsApp Report Dialog - se abre tras guardar */}
          <WhatsappReportDialog
            open={openWhatsappDialog}
            onClose={() => setOpenWhatsappDialog(false)}
            report={lastReport}
            chatterName={user?.name || user?.username}
          />

          {/* Edit Report Dialog */}
          <Dialog open={openEditReportDialog} onOpenChange={setOpenEditReportDialog}>
            <DialogContent className="w-[95vw] max-w-lg bg-zinc-900 border-zinc-800 max-h-[90vh] overflow-y-auto overflow-x-hidden">
              <DialogHeader>
                <DialogTitle className="text-white">Editar Reporte Diario</DialogTitle>
              </DialogHeader>
              {editingReport && (
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="edit-report-date" className="text-gray-300">Fecha</Label>
                    <Input
                      id="edit-report-date"
                      type="date"
                      value={editingReport.date}
                      onChange={(e) => setEditingReport({ ...editingReport, date: e.target.value })}
                      className="bg-zinc-800 border-zinc-700 text-white"
                    />
                  </div>

                  <div>
                    <Label htmlFor="edit-report-hours" className="text-gray-300">Horas Trabajadas</Label>
                    <Input
                      id="edit-report-hours"
                      type="number"
                      step="0.5"
                      value={editingReport.hours_worked}
                      onChange={(e) => setEditingReport({ ...editingReport, hours_worked: parseFloat(e.target.value) })}
                      className="bg-zinc-800 border-zinc-700 text-white"
                    />
                  </div>

                  <div>
                    <Label htmlFor="edit-report-money" className="text-gray-300">Dinero Total Recaudado ($)</Label>
                    <Input
                      id="edit-report-money"
                      type="number"
                      step="0.01"
                      value={editingReport.money_collected}
                      onChange={(e) => setEditingReport({ ...editingReport, money_collected: parseFloat(e.target.value) })}
                      className="bg-zinc-800 border-zinc-700 text-white"
                    />
                  </div>

                  {/* Desglose por Modelo (Edit) - TODAS las modelos activas */}
                  {(() => {
                    const activeModels = models.filter(m => m.status !== 'inactiva');
                    const modelIds = activeModels.map(m => m.id);
                    if (modelIds.length === 0) return null;
                    const validModelsById = new Map(activeModels.map(m => [m.id, m]));
                    const sbm = editingReport.sales_by_model || {};
                    const sum = modelIds.reduce((s, mid) => s + (parseFloat(sbm[mid]) || 0), 0);
                    const diff = +((editingReport.money_collected || 0) - sum).toFixed(2);
                    return (
                      <div className="border border-pink-500/30 rounded-lg p-3 space-y-2 bg-pink-500/5">
                        <div className="flex items-center justify-between">
                          <p className="text-xs text-pink-300 font-medium">👑 Distribución por Modelo</p>
                          <span className={`text-xs font-semibold ${Math.abs(diff) < 0.01 ? 'text-green-400' : 'text-orange-400'}`}>
                            ${sum.toFixed(2)} / ${(editingReport.money_collected || 0).toFixed(2)}
                          </span>
                        </div>
                        <div className="space-y-2">
                          {modelIds.map(mid => {
                            const model = models.find(m => m.id === mid);
                            return (
                              <div key={mid} className="flex items-center gap-2">
                                <Label className="text-gray-300 text-xs flex-1 truncate">{model?.name || mid}</Label>
                                <Input
                                  type="number"
                                  step="0.01"
                                  value={sbm[mid] || 0}
                                  onChange={(e) => {
                                    const v = parseFloat(e.target.value) || 0;
                                    setEditingReport({
                                      ...editingReport,
                                      sales_by_model: { ...sbm, [mid]: v }
                                    });
                                  }}
                                  className="bg-zinc-800 border-zinc-700 text-white w-32"
                                />
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })()}

                  {/* Desglose de categorías - Solo Messages y Tips para Chatters */}
                  <div className="border border-zinc-700 rounded-lg p-3 space-y-3">
                    <p className="text-xs text-gray-400 font-medium">Desglose por Categoria</p>
                    
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label htmlFor="edit-report-messages" className="text-emerald-300 text-xs">Messages ($)</Label>
                        <Input
                          id="edit-report-messages"
                          type="number"
                          step="0.01"
                          value={editingReport.messages_amount || 0}
                          onChange={(e) => setEditingReport({ ...editingReport, messages_amount: parseFloat(e.target.value) || 0 })}
                          className="bg-zinc-800 border-zinc-700 text-white text-sm"
                          placeholder="$0"
                        />
                      </div>
                      <div>
                        <Label htmlFor="edit-report-tips" className="text-blue-300 text-xs">Tips ($)</Label>
                        <Input
                          id="edit-report-tips"
                          type="number"
                          step="0.01"
                          value={editingReport.tips_amount || 0}
                          onChange={(e) => setEditingReport({ ...editingReport, tips_amount: parseFloat(e.target.value) || 0 })}
                          className="bg-zinc-800 border-zinc-700 text-white text-sm"
                          placeholder="$0"
                        />
                      </div>
                    </div>
                  </div>

                  <Button
                    onClick={handleEditReport}
                    className="w-full bg-blue-600 hover:bg-blue-700"
                  >
                    💾 Guardar Cambios
                  </Button>
                </div>
              )}
            </DialogContent>
          </Dialog>
        </div>

        {/* Payslips Section */}
        <Card className="bg-[#0a0a0a] border border-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-2xl mb-8">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Wallet className="w-5 h-5" />
              Mis Colillas de Pago
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {payslips.length === 0 ? (
                <div className="text-center text-gray-400 py-8">
                  No tienes colillas de pago registradas
                </div>
              ) : (
                payslips.map((payslip) => (
                  <Card key={payslip.id} className={`${payslip.pagado ? 'bg-green-500/5 border-green-500/40' : 'bg-zinc-900/50 border-white/5'} border`}>
                    <CardContent className="pt-4">
                      <div className="space-y-3">
                        {/* Header */}
                        <div className="flex justify-between items-start border-b border-white/10 pb-3 gap-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 flex-wrap mb-1">
                              <h3 className="text-white font-bold text-lg">{payslip.periodo}</h3>
                              {payslip.pagado ? (
                                <span className="px-2 py-0.5 text-xs rounded-full bg-green-500/20 text-green-400 border border-green-500/30 font-semibold" data-testid={`my-payslip-paid-${payslip.id}`}>
                                  ✓ Pagada
                                </span>
                              ) : (
                                <span className="px-2 py-0.5 text-xs rounded-full bg-amber-500/20 text-amber-400 border border-amber-500/30 font-semibold" data-testid={`my-payslip-pending-${payslip.id}`}>
                                  ⏳ Pendiente de pago
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-400">
                              {new Date(payslip.fecha_inicio).toLocaleDateString('es')} - {new Date(payslip.fecha_fin).toLocaleDateString('es')}
                            </p>
                            {payslip.pagado && payslip.fecha_pago && (
                              <p className="text-xs text-green-400 mt-1 font-medium">
                                ✓ Pagada el {new Date(payslip.fecha_pago).toLocaleDateString('es', { day: '2-digit', month: 'short', year: 'numeric' })}
                              </p>
                            )}
                          </div>
                        </div>

                        {/* Salary Details */}
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-400">Salario Base (Quincenal):</span>
                            <span className="text-white font-medium">${payslip.salario_base.toLocaleString()} COP</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-400">Dias de Quincena:</span>
                            <span className="text-white">{payslip.dias_quincena} días</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-400">Valor por Día:</span>
                            <span className="text-white">${payslip.valor_dia.toLocaleString()} COP</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-400">Días Trabajados:</span>
                            <span className="text-white font-semibold">{payslip.dias_trabajados} días</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-400">Horas Trabajadas:</span>
                            <span className="text-white">{payslip.horas_trabajadas}h</span>
                          </div>
                        </div>

                        {/* Calculation */}
                        <div className="border-t border-zinc-700 pt-3 space-y-2">
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-400">Salario Devengado:</span>
                            <span className="text-blue-400 font-semibold">${payslip.salario_devengado.toLocaleString()} COP</span>
                          </div>

                          {payslip.bonificaciones > 0 && (
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-400">Bonificaciones:</span>
                              <span className="text-green-400">+${payslip.bonificaciones.toLocaleString()} COP</span>
                            </div>
                          )}

                          {payslip.deducciones > 0 && (
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-400">Deducciones:</span>
                              <span className="text-red-400">-${payslip.deducciones.toLocaleString()} COP</span>
                            </div>
                          )}
                        </div>
                        
                        {/* Total */}
                        <div className="flex justify-between text-lg font-bold border-t border-zinc-700 pt-3 bg-zinc-900 rounded p-3">
                          <span className="text-white">Total a Recibir:</span>
                          <span className="text-green-500">${payslip.total_pagado.toLocaleString()} COP</span>
                        </div>
                        
                        {payslip.notas && (
                          <div className="mt-3 p-3 bg-zinc-900 rounded text-sm">
                            <p className="text-gray-400 font-semibold">Notas:</p>
                            <p className="text-gray-300 mt-1">{payslip.notas}</p>
                          </div>
                        )}

                        {payslip.observaciones && (
                          <div className="mt-2 p-3 bg-amber-500/10 border border-amber-500/30 rounded text-sm" data-testid={`my-payslip-obs-${payslip.id}`}>
                            <p className="text-amber-400 font-semibold">📝 Observación del administrador:</p>
                            <p className="text-amber-200 mt-1 whitespace-pre-wrap">{payslip.observaciones}</p>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </CardContent>
        </Card>

        {/* Reports List */}
        <Card className="bg-[#0a0a0a] border border-white/[0.08] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_4px_rgba(0,0,0,0.5),0_12px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] rounded-2xl">
          <CardHeader>
            <CardTitle className="text-white">Mis Reportes (Mes Actual)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 max-h-96 overflow-y-auto" data-testid="reports-list">
              {currentMonthReports.length === 0 ? (
                <div className="text-center text-gray-400 py-8">
                  No has creado reportes este mes
                </div>
              ) : (
                currentMonthReports.map((report) => (
                  <div key={report.id} className="p-4 bg-zinc-900/50 border border-white/5 rounded-xl space-y-2 hover:bg-white/5 transition-colors">
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="font-semibold text-white">{report.group_name}</div>
                        <div className="text-sm text-gray-400">{report.date}</div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          onClick={() => {
                            setEditingReport(report);
                            setOpenEditReportDialog(true);
                          }}
                          size="sm"
                          variant="outline"
                          className="text-blue-400 border-blue-500/50 hover:bg-blue-900/20"
                        >
                          Editar
                        </Button>
                        <Button
                          onClick={() => handleDeleteReport(report.id)}
                          size="sm"
                          variant="destructive"
                          className="bg-red-600 hover:bg-red-700"
                        >
                          X
                        </Button>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-400">Horas: </span>
                        <span className="font-semibold text-white">{report.hours_worked} hrs</span>
                      </div>
                      <div>
                        <span className="text-gray-400">Dinero: </span>
                        <span className="font-semibold text-green-400">${report.money_collected}</span>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
            </>
          )}

          {/* Spenders Tab */}
          {activeTab === 'spenders' && (
            <div className="space-y-6">
              {crmStats && (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <Card className="bg-[#0a0a0a] border border-white/[0.08] rounded-2xl p-4">
                    <p className="text-gray-400 text-sm">Total Usuarios</p>
                    <p className="text-3xl font-bold text-white">{crmStats.total}</p>
                  </Card>
                  <Card className="bg-[#0a0a0a] border border-green-500/20 rounded-2xl p-4">
                    <p className="text-green-400 text-sm">High Spenders</p>
                    <p className="text-3xl font-bold text-green-400">{crmStats.by_spending_level?.high || 0}</p>
                  </Card>
                  <Card className="bg-[#0a0a0a] border border-yellow-500/20 rounded-2xl p-4">
                    <p className="text-yellow-400 text-sm">Medium</p>
                    <p className="text-3xl font-bold text-yellow-400">{crmStats.by_spending_level?.medium || 0}</p>
                  </Card>
                  <Card className="bg-[#0a0a0a] border border-gray-500/20 rounded-2xl p-4">
                    <p className="text-gray-400 text-sm">Low</p>
                    <p className="text-3xl font-bold text-gray-300">{crmStats.by_spending_level?.low || 0}</p>
                  </Card>
                  <Card
                    className={`bg-[#0a0a0a] border rounded-2xl p-4 cursor-pointer transition-all ${
                      crmFilterCashback
                        ? 'border-red-500 shadow-[0_0_0_1px_rgba(239,68,68,0.4)]'
                        : 'border-red-500/20 hover:border-red-500/50'
                    }`}
                    onClick={() => setCrmFilterCashback((v) => !v)}
                  >
                    <p className="text-red-400 text-sm">🚨 Cashbacks</p>
                    <p className="text-3xl font-bold text-red-400">{crmStats.cashback_count || 0}</p>
                  </Card>
                </div>
              )}

              <Card className="bg-[#0a0a0a] border border-white/[0.08] rounded-2xl">
                <CardHeader className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                  <CardTitle className="text-white">Base de Datos</CardTitle>
                  <Dialog open={openCrmDialog} onOpenChange={setOpenCrmDialog}>
                    <DialogTrigger asChild>
                      <Button className="bg-blue-600 hover:bg-blue-700">+ Nuevo Usuario</Button>
                    </DialogTrigger>
                    <DialogContent className="bg-zinc-900 border-zinc-700 text-white max-w-lg">
                      <DialogHeader><DialogTitle>Agregar Usuario</DialogTitle></DialogHeader>
                      <div className="space-y-4 py-4 max-h-[70vh] overflow-y-auto">
                        <div>
                          <Label className="text-gray-300">Username *</Label>
                          <Input value={newCrmUser.username} onChange={(e) => setNewCrmUser({ ...newCrmUser, username: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white" placeholder="@usuario" />
                        </div>
                        <div>
                          <Label className="text-gray-300">Modelo *</Label>
                          <Select value={newCrmUser.modelo_id} onValueChange={(v) => setNewCrmUser({ ...newCrmUser, modelo_id: v })}>
                            <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                            <SelectContent className="bg-zinc-800 border-zinc-700">
                              {models.map((m) => (<SelectItem key={m.id} value={m.id} className="text-white">{m.name}</SelectItem>))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-gray-300">Nivel de Gasto</Label>
                          <Select value={newCrmUser.nivel_gasto} onValueChange={(v) => setNewCrmUser({ ...newCrmUser, nivel_gasto: v })}>
                            <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white"><SelectValue /></SelectTrigger>
                            <SelectContent className="bg-zinc-800 border-zinc-700">
                              <SelectItem value="low" className="text-gray-300">Low</SelectItem>
                              <SelectItem value="medium" className="text-yellow-400">Medium</SelectItem>
                              <SelectItem value="high" className="text-green-400">High</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-gray-300">Origen</Label>
                          <Input value={newCrmUser.origen} onChange={(e) => setNewCrmUser({ ...newCrmUser, origen: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white" placeholder="Pais" />
                        </div>
                        <div>
                          <Label className="text-gray-300">Gustos</Label>
                          <Input value={newCrmUser.gustos} onChange={(e) => setNewCrmUser({ ...newCrmUser, gustos: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white" />
                        </div>
                        <div>
                          <Label className="text-gray-300">Solicitudes</Label>
                          <Input value={newCrmUser.solicitudes} onChange={(e) => setNewCrmUser({ ...newCrmUser, solicitudes: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white" />
                        </div>
                        <div>
                          <Label className="text-gray-300">Notas</Label>
                          <Input value={newCrmUser.notas} onChange={(e) => setNewCrmUser({ ...newCrmUser, notas: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white" />
                        </div>

                        {/* CASHBACK SECTION */}
                        <div className="border-t border-zinc-700 pt-4 mt-2 bg-red-500/5 -mx-2 px-4 py-3 rounded-lg">
                          <div className="flex items-center gap-2 mb-3">
                            <span className="text-red-400 text-lg">⚠️</span>
                            <h4 className="text-white font-semibold text-sm">Registro de Cashback (Posible Estafador)</h4>
                          </div>
                          <div className="flex items-center gap-3 mb-3">
                            <input id="emp-cashback-new" type="checkbox" checked={!!newCrmUser.hizo_cashback} onChange={(e) => setNewCrmUser({ ...newCrmUser, hizo_cashback: e.target.checked })} className="w-5 h-5 rounded accent-red-600" />
                            <label htmlFor="emp-cashback-new" className="text-gray-300 text-sm cursor-pointer">Este usuario realizó un cashback / chargeback</label>
                          </div>
                          {newCrmUser.hizo_cashback && (
                            <div className="space-y-3">
                              <div className="grid grid-cols-2 gap-3">
                                <div><Label className="text-gray-300 text-xs">Fecha</Label>
                                  <Input type="date" value={newCrmUser.fecha_cashback || ''} onChange={(e) => setNewCrmUser({ ...newCrmUser, fecha_cashback: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white" />
                                </div>
                                <div><Label className="text-gray-300 text-xs">Monto ($)</Label>
                                  <Input type="number" step="0.01" value={newCrmUser.monto_cashback || 0} onChange={(e) => setNewCrmUser({ ...newCrmUser, monto_cashback: parseFloat(e.target.value) || 0 })} className="bg-zinc-800 border-zinc-700 text-white" />
                                </div>
                              </div>
                              <div><Label className="text-gray-300 text-xs">Notas del cashback</Label>
                                <Input value={newCrmUser.notas_cashback || ''} onChange={(e) => setNewCrmUser({ ...newCrmUser, notas_cashback: e.target.value })} placeholder="Razón, plataforma, evidencia..." className="bg-zinc-800 border-zinc-700 text-white" />
                              </div>
                            </div>
                          )}
                        </div>

                        <Button onClick={handleCreateCrmUser} className="w-full bg-blue-600 hover:bg-blue-700">Guardar</Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-col md:flex-row gap-4 mb-6">
                    <Input placeholder="Buscar username..." value={crmSearchTerm} onChange={(e) => setCrmSearchTerm(e.target.value)} className="bg-zinc-800 border-zinc-700 text-white flex-1" />
                    <Select value={crmFilterGasto} onValueChange={setCrmFilterGasto}>
                      <SelectTrigger className="w-full md:w-40 bg-zinc-800 border-zinc-700 text-white"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-zinc-800 border-zinc-700">
                        <SelectItem value="all" className="text-white">Todos</SelectItem>
                        <SelectItem value="high" className="text-green-400">High</SelectItem>
                        <SelectItem value="medium" className="text-yellow-400">Medium</SelectItem>
                        <SelectItem value="low" className="text-gray-300">Low</SelectItem>
                      </SelectContent>
                    </Select>
                    <button
                      type="button"
                      onClick={() => setCrmFilterCashback((v) => !v)}
                      className={`px-4 py-2 rounded-lg border text-sm font-medium transition-all whitespace-nowrap ${
                        crmFilterCashback
                          ? 'bg-red-500/20 border-red-500 text-red-400'
                          : 'bg-zinc-800 border-zinc-700 text-gray-400 hover:border-red-500/50'
                      }`}
                    >
                      🚨 Solo Cashback
                    </button>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-white/10">
                          <th className="text-left py-3 px-2 text-gray-400 font-medium">Username</th>
                          <th className="text-left py-3 px-2 text-gray-400 font-medium">Modelo</th>
                          <th className="text-left py-3 px-2 text-gray-400 font-medium">Nivel</th>
                          <th className="text-left py-3 px-2 text-gray-400 font-medium">Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {crmUsers.length === 0 ? (
                          <tr><td colSpan="4" className="text-center py-8 text-gray-500">No hay usuarios</td></tr>
                        ) : (
                          crmUsers.map((u) => (
                            <tr key={u.id} className={`border-b border-white/5 hover:bg-white/5 ${u.hizo_cashback ? 'bg-red-500/5' : ''}`}>
                              <td className="py-3 px-2 text-white">
                                <div className="flex items-center gap-2">
                                  <span>{u.username}</span>
                                  {u.hizo_cashback && (
                                    <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-500/20 text-red-400 border border-red-500/40" title={u.notas_cashback || 'Marcado por cashback'}>
                                      🚨 ESTAFADOR
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td className="py-3 px-2 text-gray-300">{u.modelo_name}</td>
                              <td className="py-3 px-2">
                                <span className={`px-2 py-1 rounded-full text-xs ${u.nivel_gasto === 'high' ? 'bg-green-500/20 text-green-400' : u.nivel_gasto === 'medium' ? 'bg-yellow-500/20 text-yellow-400' : 'bg-gray-500/20 text-gray-400'}`}>
                                  {u.nivel_gasto}
                                </span>
                              </td>
                              <td className="py-3 px-2">
                                <div className="flex gap-2">
                                  <Button size="sm" variant="outline" className="border-blue-500/50 text-blue-400" onClick={() => setEditingCrmUser(u)}>Ver</Button>
                                  <Button size="sm" variant="outline" className="border-red-500/50 text-red-400" onClick={() => handleDeleteCrmUser(u.id)}>X</Button>
                                </div>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>

              <Dialog open={!!editingCrmUser} onOpenChange={() => setEditingCrmUser(null)}>
                <DialogContent className="bg-zinc-900 border-zinc-700 text-white max-w-lg">
                  <DialogHeader><DialogTitle>Editar: {editingCrmUser?.username}</DialogTitle></DialogHeader>
                  {editingCrmUser && (
                    <div className="space-y-4 py-4">
                      <div><Label className="text-gray-300">Username</Label><Input value={editingCrmUser.username} onChange={(e) => setEditingCrmUser({ ...editingCrmUser, username: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white" /></div>
                      <div><Label className="text-gray-300">Nivel de Gasto</Label>
                        <Select value={editingCrmUser.nivel_gasto} onValueChange={(v) => setEditingCrmUser({ ...editingCrmUser, nivel_gasto: v })}>
                          <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white"><SelectValue /></SelectTrigger>
                          <SelectContent className="bg-zinc-800 border-zinc-700">
                            <SelectItem value="low" className="text-gray-300">Low</SelectItem>
                            <SelectItem value="medium" className="text-yellow-400">Medium</SelectItem>
                            <SelectItem value="high" className="text-green-400">High</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div><Label className="text-gray-300">Origen</Label><Input value={editingCrmUser.origen || ''} onChange={(e) => setEditingCrmUser({ ...editingCrmUser, origen: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white" /></div>
                      <div><Label className="text-gray-300">Gustos</Label><Input value={editingCrmUser.gustos || ''} onChange={(e) => setEditingCrmUser({ ...editingCrmUser, gustos: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white" /></div>
                      <div><Label className="text-gray-300">Solicitudes</Label><Input value={editingCrmUser.solicitudes || ''} onChange={(e) => setEditingCrmUser({ ...editingCrmUser, solicitudes: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white" /></div>
                      <div><Label className="text-gray-300">Notas</Label><Input value={editingCrmUser.notas || ''} onChange={(e) => setEditingCrmUser({ ...editingCrmUser, notas: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white" /></div>

                      {/* CASHBACK SECTION (EDIT) */}
                      <div className="border-t border-zinc-700 pt-4 mt-2 bg-red-500/5 -mx-2 px-4 py-3 rounded-lg">
                        <div className="flex items-center gap-2 mb-3">
                          <span className="text-red-400 text-lg">⚠️</span>
                          <h4 className="text-white font-semibold text-sm">Registro de Cashback (Posible Estafador)</h4>
                        </div>
                        <div className="flex items-center gap-3 mb-3">
                          <input id="emp-cashback-edit" type="checkbox" checked={!!editingCrmUser.hizo_cashback} onChange={(e) => setEditingCrmUser({ ...editingCrmUser, hizo_cashback: e.target.checked })} className="w-5 h-5 rounded accent-red-600" />
                          <label htmlFor="emp-cashback-edit" className="text-gray-300 text-sm cursor-pointer">Este usuario realizó un cashback / chargeback</label>
                        </div>
                        {editingCrmUser.hizo_cashback && (
                          <div className="space-y-3">
                            <div className="grid grid-cols-2 gap-3">
                              <div><Label className="text-gray-300 text-xs">Fecha</Label>
                                <Input type="date" value={editingCrmUser.fecha_cashback || ''} onChange={(e) => setEditingCrmUser({ ...editingCrmUser, fecha_cashback: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white" />
                              </div>
                              <div><Label className="text-gray-300 text-xs">Monto ($)</Label>
                                <Input type="number" step="0.01" value={editingCrmUser.monto_cashback || 0} onChange={(e) => setEditingCrmUser({ ...editingCrmUser, monto_cashback: parseFloat(e.target.value) || 0 })} className="bg-zinc-800 border-zinc-700 text-white" />
                              </div>
                            </div>
                            <div><Label className="text-gray-300 text-xs">Notas del cashback</Label>
                              <Input value={editingCrmUser.notas_cashback || ''} onChange={(e) => setEditingCrmUser({ ...editingCrmUser, notas_cashback: e.target.value })} placeholder="Razón, plataforma, evidencia..." className="bg-zinc-800 border-zinc-700 text-white" />
                            </div>
                          </div>
                        )}
                      </div>

                      <Button onClick={handleUpdateCrmUser} className="w-full bg-blue-600 hover:bg-blue-700">Guardar</Button>
                    </div>
                  )}
                </DialogContent>
              </Dialog>
            </div>
          )}

          {/* Creadoras Tab */}
          {activeTab === 'creadoras' && (
            <CreadoresViewPanel />
          )}

          {/* Calculadora Tab */}
          {activeTab === 'calculadora' && (
            <CustomCalculator />
          )}

          {/* Metas Tab (solo lectura, sin metas de empleados) */}
          {activeTab === 'metas' && (
            <MetasPanel readOnly={true} hideEmployeeGoals={true} chatterMode={true} />
          )}

          {/* Solicitudes Tab */}
          {activeTab === 'solicitudes' && (
            <SolicitudesPanel currentUserRole={user?.role} />
          )}

          {/* Colillas Tab */}
          {activeTab === 'colillas' && (
            <div className="space-y-6">
              <Card className="bg-[#0a0a0a] border border-white/[0.08] rounded-2xl">
                <CardHeader>
                  <CardTitle className="text-white">Mis Colillas de Pago</CardTitle>
                </CardHeader>
                <CardContent>
                  {payslips.length === 0 ? (
                    <p className="text-center text-gray-500 py-8">No tienes colillas de pago aun</p>
                  ) : (
                    <div className="space-y-4">
                      {payslips.map((payslip) => (
                        <Card key={payslip.id} className="bg-zinc-900 border-zinc-700">
                          <CardContent className="p-4">
                            <div className="flex justify-between items-center mb-4">
                              <h3 className="text-lg font-bold text-white">{payslip.periodo}</h3>
                              <span className="text-green-400 font-bold text-xl">${payslip.total_pagado?.toLocaleString()} COP</span>
                            </div>
                            <div className="grid grid-cols-2 gap-4 text-sm">
                              <div><span className="text-gray-400">Salario Base:</span> <span className="text-white">${payslip.salario_base?.toLocaleString()}</span></div>
                              <div><span className="text-gray-400">Dias Trabajados:</span> <span className="text-white">{payslip.dias_trabajados}</span></div>
                              <div><span className="text-gray-400">Horas:</span> <span className="text-white">{payslip.horas_trabajadas?.toFixed(1)}</span></div>
                              <div><span className="text-gray-400">Bonificaciones:</span> <span className="text-green-400">${payslip.bonificaciones?.toLocaleString()}</span></div>
                              <div><span className="text-gray-400">Deducciones:</span> <span className="text-red-400">${payslip.deducciones?.toLocaleString()}</span></div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </div>

      {/* Profile Dialog */}
      <Dialog open={openProfileDialog} onOpenChange={setOpenProfileDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-700 text-white">
          <DialogHeader><DialogTitle>Mi Perfil</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="flex flex-col items-center gap-4">
              {profileData.profile_picture ? (
                <img src={profileData.profile_picture} alt="" className="w-24 h-24 rounded-full object-cover" />
              ) : (
                <div className="w-24 h-24 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                  <span className="text-white text-3xl font-bold">{user?.name?.charAt(0)}</span>
                </div>
              )}
              <label className="cursor-pointer">
                <span className="text-blue-400 text-sm hover:underline">{uploadingPicture ? 'Subiendo...' : 'Cambiar foto'}</span>
                <input type="file" accept="image/*" onChange={handleProfilePictureUpload} className="hidden" disabled={uploadingPicture} />
              </label>
            </div>
            <div><Label className="text-gray-300">Nombre</Label><Input value={profileData.name} onChange={(e) => setProfileData({ ...profileData, name: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white" /></div>
            <div><Label className="text-gray-300">Telefono</Label><Input value={profileData.phone} onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white" /></div>
            <Button onClick={handleUpdateProfile} className="w-full bg-blue-600 hover:bg-blue-700">Guardar</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Team Chat Modal */}
      <TeamChat 
        isOpen={teamChatOpen} 
        onClose={() => {
          setTeamChatOpen(false);
          fetchChatUnreadCount();
        }}
        currentUser={user}
      />
    </div>
  );
}

export default EmployeeDashboard;